// @ts-nocheck
/**
 * ドラフトシステム - メイン状態管理フック
 */

import { useState, useCallback, useEffect } from 'react';
import { DRAFT_STATUS } from '../constants';
import {
  createDraftSession,
  executePickRound,
  generateSummary,
} from '../systems/draftManager';
import {
  saveDraftSession,
  loadDraftSession,
  clearDraftSession,
  restoreDraftSession,
  hasSavedSession,
  getDraftSummary,
} from '../systems/draftStorage';

/**
 * ドラフトメインフック
 * @param {Object[]} allCards - 全カードデータ
 * @param {Map} [cardValueMap] - カード価値マップ
 * @returns {Object} ドラフト状態と操作関数
 */
export const useDraft = (allCards, cardValueMap = null) => {
  // ========================================
  // 状態
  // ========================================
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // ========================================
  // 初期化
  // ========================================

  /**
   * 保存セッションをチェック
   */
  useEffect(() => {
    if (!allCards || allCards.length === 0) return;

    setIsLoading(true);
    try {
      // 保存セッションがあるかチェック
      if (hasSavedSession()) {
        // 自動復元はしない（ユーザーに選択させる）
        console.log('[useDraft] Saved session found');
      }
    } catch (e) {
      console.error('[useDraft] Check failed:', e);
    } finally {
      setIsLoading(false);
    }
  }, [allCards]);

  // ========================================
  // セッション操作
  // ========================================

  /**
   * 新規ドラフトを開始
   * @param {number} playerCount - 参加人数
   */
  const startNewDraft = useCallback((playerCount = 4) => {
    if (!allCards || allCards.length === 0) {
      setError('カードデータが読み込まれていません');
      return;
    }

    try {
      // 既存セッションをクリア
      clearDraftSession();

      // 新規セッション作成
      const newSession = createDraftSession(allCards, playerCount, cardValueMap);
      setSession(newSession);
      setError(null);

      // 保存
      saveDraftSession(newSession);

      console.log('[useDraft] New draft started:', newSession.sessionId);
    } catch (e) {
      console.error('[useDraft] Start failed:', e);
      setError('ドラフトの開始に失敗しました');
    }
  }, [allCards, cardValueMap]);

  /**
   * 保存セッションを復元
   */
  const resumeDraft = useCallback(() => {
    if (!allCards || allCards.length === 0) {
      setError('カードデータが読み込まれていません');
      return false;
    }

    try {
      const restoredSession = restoreDraftSession(allCards);
      if (restoredSession) {
        setSession(restoredSession);
        setError(null);
        console.log('[useDraft] Session resumed:', restoredSession.sessionId);
        return true;
      } else {
        setError('保存セッションが見つかりません');
        return false;
      }
    } catch (e) {
      console.error('[useDraft] Resume failed:', e);
      setError('セッションの復元に失敗しました');
      return false;
    }
  }, [allCards]);

  /**
   * ドラフトをキャンセル
   */
  const cancelDraft = useCallback(() => {
    clearDraftSession();
    setSession(null);
    setError(null);
    console.log('[useDraft] Draft cancelled');
  }, []);

  // ========================================
  // ピック操作
  // ========================================

  /**
   * プレイヤーがカードをピック
   * @param {string} cardId - ピックするカードID
   */
  const pickCard = useCallback((cardId) => {
    if (!session) {
      setError('セッションが開始されていません');
      return false;
    }

    if (session.status !== DRAFT_STATUS.PICKING) {
      setError('現在ピックフェーズではありません');
      return false;
    }

    try {
      // ピック処理を実行
      const newSession = executePickRound(session, cardId);
      setSession(newSession);

      // 保存
      saveDraftSession(newSession);

      console.log('[useDraft] Card picked:', cardId);
      return true;
    } catch (e) {
      console.error('[useDraft] Pick failed:', e);
      setError('ピックに失敗しました');
      return false;
    }
  }, [session]);

  // ========================================
  // デッキ構築操作
  // ========================================

  /**
   * カードをデッキに追加
   * @param {string} cardId - 追加するカードID
   */
  const addToDeck = useCallback((cardId) => {
    if (!session || !session.deckBuilding) return false;

    try {
      const newSession = JSON.parse(JSON.stringify(session));
      const db = newSession.deckBuilding.player;

      // サイドボードからカードを探す
      const cardIndex = db.sideboard.findIndex(c => c.id === cardId);
      if (cardIndex === -1) return false;

      // デッキに移動
      const card = db.sideboard.splice(cardIndex, 1)[0];
      db.deck.push(card);

      setSession(newSession);
      saveDraftSession(newSession);
      return true;
    } catch (e) {
      console.error('[useDraft] addToDeck failed:', e);
      return false;
    }
  }, [session]);

  /**
   * カードをサイドボードに戻す
   * @param {string} cardId - 戻すカードID
   */
  const removeFromDeck = useCallback((cardId) => {
    if (!session || !session.deckBuilding) return false;

    try {
      const newSession = JSON.parse(JSON.stringify(session));
      const db = newSession.deckBuilding.player;

      // デッキからカードを探す
      const cardIndex = db.deck.findIndex(c => c.id === cardId);
      if (cardIndex === -1) return false;

      // サイドボードに戻す
      const card = db.deck.splice(cardIndex, 1)[0];
      db.sideboard.push(card);

      setSession(newSession);
      saveDraftSession(newSession);
      return true;
    } catch (e) {
      console.error('[useDraft] removeFromDeck failed:', e);
      return false;
    }
  }, [session]);

  /**
   * デッキを確定
   */
  const confirmDeck = useCallback(() => {
    if (!session || !session.deckBuilding) return false;

    try {
      const newSession = JSON.parse(JSON.stringify(session));
      newSession.deckBuilding.player.isConfirmed = true;

      // 全員確定したらトーナメントへ
      const allConfirmed = Object.values(newSession.deckBuilding)
        .every(db => db.isConfirmed);

      if (allConfirmed) {
        newSession.status = DRAFT_STATUS.TOURNAMENT;
      }

      setSession(newSession);
      saveDraftSession(newSession);
      return true;
    } catch (e) {
      console.error('[useDraft] confirmDeck failed:', e);
      return false;
    }
  }, [session]);

  // ========================================
  // ユーティリティ
  // ========================================

  /**
   * 現在のプレイヤーのパックを取得
   */
  const getCurrentPack = useCallback(() => {
    if (!session || !session.draft.packs) return [];
    return session.draft.packs.player || [];
  }, [session]);

  /**
   * プレイヤーのピック済みカードを取得
   */
  const getPlayerPicks = useCallback(() => {
    if (!session || !session.draft.picks) return [];
    return session.draft.picks.player || [];
  }, [session]);

  /**
   * サマリーを取得
   */
  const getSummary = useCallback(() => {
    if (!session) return null;
    return generateSummary(session);
  }, [session]);

  /**
   * 保存セッションのサマリーを取得
   */
  const getSavedSummary = useCallback(() => {
    return getDraftSummary();
  }, []);

  /**
   * 保存セッションがあるかチェック
   */
  const checkSavedSession = useCallback(() => {
    return hasSavedSession();
  }, []);

  /**
   * トーナメント状態を更新してセッションに保存
   * @param {Object} tournamentState - トーナメント状態
   */
  const updateTournamentState = useCallback((tournamentState) => {
    if (!session) return;

    const newSession = {
      ...session,
      tournament: tournamentState,
    };
    setSession(newSession);
    saveDraftSession(newSession);
  }, [session]);

  /**
   * セッションからトーナメント状態を取得
   */
  const getTournamentState = useCallback(() => {
    return session?.tournament || null;
  }, [session]);

  // ========================================
  // 返却値
  // ========================================
  return {
    // 状態
    session,
    isLoading,
    error,

    // セッション操作
    startNewDraft,
    resumeDraft,
    cancelDraft,

    // ピック操作
    pickCard,

    // デッキ構築操作
    addToDeck,
    removeFromDeck,
    confirmDeck,

    // ユーティリティ
    getCurrentPack,
    getPlayerPicks,
    getSummary,
    getSavedSummary,
    checkSavedSession,

    // トーナメント状態
    updateTournamentState,
    getTournamentState,

    // 便利なプロパティ
    status: session?.status || null,
    isActive: session !== null,
    isPicking: session?.status === DRAFT_STATUS.PICKING,
    isBuilding: session?.status === DRAFT_STATUS.BUILDING,
    isTournament: session?.status === DRAFT_STATUS.TOURNAMENT,
    isFinished: session?.status === DRAFT_STATUS.FINISHED,
  };
};

export default useDraft;
